/**
 * Resmo Talent Directory — the Candidates Store.
 *
 * A dedicated storefront for employers/recruiters to browse published
 * candidates. Richer than /discover:
 *   · Sidebar filters — skills, years-range, location, availability,
 *     remote mode, salary range, certifications, featured-only
 *   · Grid ↔ List view toggle (persisted in localStorage)
 *   · LLM Skills Extractor widget at the top (paste JD → chips + playbook)
 *   · Superpower highlighted on every card (from network_pitches or
 *     candidate-set override on the publication row)
 *   · Pending-employer gate: anonymized preview + "verify to contact" CTA
 */
import React, { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  Search, MapPin, Users, LogOut, ArrowRight, Grid, List, Filter,
  Sparkles, Star, Zap, ChevronDown, ChevronUp, X, ShieldCheck,
  DollarSign, Home, Award, Briefcase,
} from "lucide-react";
import { useEmployer } from "../components/employer/EmployerProtected";
import SkillsExtractorWidget from "../components/employer/SkillsExtractorWidget";
import SaveCandidateButton from "../components/employer/SaveCandidateButton";
import ImpersonationBanner from "../components/employer/ImpersonationBanner";
import {
  NAVY, ORANGE, MUSTARD, CREAM,
  MIDNIGHT_BG_STYLE, GLASS_HEADER_STYLE, CARD_DEEP_SHADOW,
} from "../components/employer/employerTheme";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const AVAILABILITY_OPTIONS = [
  { value: "", label: "Any" },
  { value: "actively_looking", label: "Actively looking" },
  { value: "open_to_offers",   label: "Open to offers" },
  { value: "not_looking",       label: "Not looking" },
];
const REMOTE_MODE_OPTIONS = [
  { value: "",       label: "Any mode" },
  { value: "remote", label: "Remote" },
  { value: "hybrid", label: "Hybrid" },
  { value: "onsite", label: "On-site" },
];
const SORT_OPTIONS = [
  { value: "newest",         label: "Newest first" },
  { value: "most_viewed",    label: "Most viewed" },
  { value: "featured_first", label: "Featured first" },
  { value: "relevance",      label: "Best match" },
];

const readViewPref = () => {
  try { return localStorage.getItem("resmo_store_view") || "grid"; } catch { return "grid"; }
};

export default function CandidatesStore() {
  const navigate = useNavigate();
  const { employer, loading: employerLoading } = useEmployer();

  // Filters
  const [q, setQ] = useState("");
  const [location, setLocation] = useState("");
  const [minYears, setMinYears] = useState(0);
  const [maxYears, setMaxYears] = useState("");
  const [skills, setSkills] = useState([]);           // array of strings
  const [skillsInput, setSkillsInput] = useState(""); // free-text add
  const [availability, setAvailability] = useState("");
  const [remoteMode, setRemoteMode] = useState("");
  const [salaryMin, setSalaryMin] = useState("");
  const [salaryMax, setSalaryMax] = useState("");
  const [certs, setCerts] = useState([]);
  const [certsInput, setCertsInput] = useState("");
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [sort, setSort] = useState("newest");

  // Results + view state
  const [results, setResults] = useState([]);
  const [count, setCount] = useState(null);
  const [isVerified, setIsVerified] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [view, setView] = useState(readViewPref); // "grid" | "list"
  const [filtersOpen, setFiltersOpen] = useState(true);
  // Which of the current-page public_ids are already on this employer's
  // shortlist — populated after the search returns so each card can render
  // the correct star state without a per-card fetch.
  const [savedSet, setSavedSet] = useState(new Set());

  useEffect(() => {
    try { localStorage.setItem("resmo_store_view", view); } catch { /* no-op */ }
  }, [view]);

  const runSearch = useCallback(async () => {
    setBusy(true);
    setError("");
    try {
      const payload = {
        q, location,
        min_years: Number(minYears) || 0,
        max_years: maxYears === "" ? null : Number(maxYears),
        skills,
        availability: availability || null,
        remote_mode:  remoteMode || null,
        salary_min:   salaryMin === "" ? null : Number(salaryMin),
        salary_max:   salaryMax === "" ? null : Number(salaryMax),
        certifications: certs,
        featured_only: featuredOnly,
        sort,
        limit: 48,
      };
      const r = await axios.post(
        `${BACKEND_URL}/api/discover/store/search`,
        payload,
        { withCredentials: true },
      );
      const rows = r.data.results || [];
      setResults(rows);
      setCount(r.data.count);
      setIsVerified(!!r.data.is_verified);
      // Batch-fetch shortlist status for the visible cards. Only makes
      // sense when we're a verified employer AND we actually have public_ids
      // (pending employers see anonymized rows with public_id stripped).
      const ids = rows.map((row) => row.public_id).filter(Boolean);
      if (r.data.is_verified && ids.length) {
        try {
          const s = await axios.post(
            `${BACKEND_URL}/api/employer/shortlist/status/batch`,
            { public_ids: ids },
            { withCredentials: true },
          );
          setSavedSet(new Set(s.data.saved || []));
        } catch {
          setSavedSet(new Set());
        }
      } else {
        setSavedSet(new Set());
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || "Search failed. Please retry.";
      setError(msg);
      setResults([]);
      setCount(0);
    } finally {
      setBusy(false);
    }
  }, [q, location, minYears, maxYears, skills, availability, remoteMode,
      salaryMin, salaryMax, certs, featuredOnly, sort]);

  useEffect(() => {
    if (!employerLoading && employer) {
      runSearch();
    }
  }, [employerLoading, employer?.id, runSearch]);

  const logout = async () => {
    // If we're running as an admin shadow, the "sign out" button should
    // clear the admin candidate JWT (not the missing employer_token).
    // Otherwise use the employer logout endpoint as normal.
    const isAdminShadow = employer?.is_admin_shadow;
    try {
      if (isAdminShadow) {
        await axios.post(`${BACKEND_URL}/api/logout`, {}, { withCredentials: true });
      } else {
        await axios.post(`${BACKEND_URL}/api/employer/logout`, {}, { withCredentials: true });
      }
    } catch { /* ignore */ }
    navigate(isAdminShadow ? "/login" : "/employer/login", { replace: true });
  };

  const addSkill = (val) => {
    const cleaned = (val || "").trim();
    if (!cleaned) return;
    setSkills((prev) => (prev.includes(cleaned) ? prev : [...prev, cleaned]));
  };
  const addSkills = (arr) => {
    if (!arr?.length) return;
    setSkills((prev) => {
      const set = new Set(prev.map((s) => s.toLowerCase()));
      const next = [...prev];
      arr.forEach((raw) => {
        const s = (raw || "").trim();
        if (s && !set.has(s.toLowerCase())) {
          next.push(s);
          set.add(s.toLowerCase());
        }
      });
      return next;
    });
  };
  const removeSkill = (s) => setSkills((prev) => prev.filter((x) => x !== s));
  const addCert = () => {
    const c = certsInput.trim();
    if (c) {
      setCerts((prev) => prev.includes(c) ? prev : [...prev, c]);
      setCertsInput("");
    }
  };
  const removeCert = (c) => setCerts((prev) => prev.filter((x) => x !== c));

  const clearFilters = () => {
    setQ(""); setLocation(""); setMinYears(0); setMaxYears("");
    setSkills([]); setSkillsInput(""); setAvailability(""); setRemoteMode("");
    setSalaryMin(""); setSalaryMax(""); setCerts([]); setCertsInput("");
    setFeaturedOnly(false); setSort("newest");
  };

  return (
    <div className="min-h-screen" style={MIDNIGHT_BG_STYLE}>
      <ImpersonationBanner
        employer={employer}
        onExit={() => { window.location.href = "/admin/employers"; }}
      />
      {/* Header — glassy band with Resmo wordmark on the left */}
      <div
        className="border-b backdrop-blur-md"
        style={GLASS_HEADER_STYLE}
        data-testid="store-header"
      >
        <div className="max-w-7xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-4 min-w-0">
            {/* Resmo wordmark */}
            <Link
              to="/discover"
              className="flex items-center shrink-0"
              style={{ textDecoration: "none" }}
              data-testid="store-brand"
              title="Resmo · home"
            >
              <span
                className="font-display uppercase leading-none"
                style={{
                  fontSize: "1.6rem",
                  color: "white",
                  letterSpacing: "-0.02em",
                  fontWeight: 900,
                }}
              >
                <span style={{ color: ORANGE }}>R</span>esmo
              </span>
              <span
                className="ml-2 hidden md:inline-block font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                style={{
                  fontSize: "9px",
                  color: NAVY,
                  background: MUSTARD,
                  fontWeight: 800,
                  border: `1px solid ${MUSTARD}`,
                }}
              >
                · Talent Directory
              </span>
            </Link>
            {/* Divider dot */}
            <span className="hidden md:inline-block" style={{ color: "rgba(255,255,255,0.28)" }}>•</span>
            <div className="min-w-0">
              <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: MUSTARD, fontWeight: 700 }}>
                · Candidates Store
              </div>
              <div className="font-display uppercase leading-tight truncate" style={{ fontSize: "1.15rem", color: "white", letterSpacing: "-0.01em" }}>
                The storefront of verified talent.
              </div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to="/discover"
              className="hidden md:inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
              style={{ color: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 700, textDecoration: "none" }}
              data-testid="store-nav-discover"
            >
              <Search size={11} /> Quick search
            </Link>
            <Link
              to="/employer/shortlist"
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
              style={{ color: NAVY, background: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 800, textDecoration: "none" }}
              data-testid="store-nav-shortlist"
            >
              <Star size={11} fill={NAVY} /> Shortlist
            </Link>
            <Link
              to="/employer/requests"
              className="hidden md:inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
              style={{ color: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 700, textDecoration: "none" }}
              data-testid="store-nav-outbox"
            >
              <Briefcase size={11} /> My requests
            </Link>
            <span className="hidden md:inline-block text-[11px]" style={{ color: "#94A3B8" }}>
              {employer?.company} · {employer?.email}
            </span>
            <button
              onClick={logout}
              className="inline-flex items-center gap-1 rounded px-2 py-1 text-[10px] font-mono-rm uppercase tracking-widest"
              style={{ background: "transparent", color: MUSTARD, border: `1px solid ${MUSTARD}`, fontWeight: 700 }}
              data-testid="store-logout"
            >
              <LogOut size={11} /> Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Pending-employer notice */}
      {!isVerified && (
        <div
          className="max-w-7xl mx-auto px-4 md:px-8 pt-4"
          data-testid="store-pending-banner"
        >
          <div
            className="rounded-md p-3 flex items-start gap-2"
            style={{ background: CREAM, border: `1.5px dashed ${ORANGE}`, color: NAVY }}
          >
            <ShieldCheck size={16} style={{ color: ORANGE, marginTop: 2 }} />
            <div className="text-[12px] leading-relaxed">
              <div className="font-mono-rm uppercase tracking-widest text-[10px] mb-1" style={{ color: ORANGE, fontWeight: 700 }}>
                · Pending verification
              </div>
              You&apos;re browsing an anonymized preview. Verify your work email to unlock
              full candidate profiles + the &ldquo;Send Introduction&rdquo; contact relay.
              <Link
                to="/employer/pending"
                className="ml-2 underline"
                style={{ color: ORANGE, fontWeight: 700 }}
                data-testid="store-pending-cta"
              >
                Verify now →
              </Link>
            </div>
          </div>
        </div>
      )}

      {/* Admin shadow ribbon — subtle, non-intrusive, tells admin they're
          viewing the employer surface via their admin JWT (not a real
          employer account). Only appears when employer.is_admin_shadow. */}
      {employer?.is_admin_shadow && (
        <div className="max-w-7xl mx-auto px-4 md:px-8 pt-3" data-testid="store-admin-ribbon">
          <div
            className="rounded-md px-3 py-2 flex items-center gap-2"
            style={{
              background: "rgba(255,193,69,0.10)",
              border: `1px dashed ${MUSTARD}`,
              color: MUSTARD,
            }}
          >
            <ShieldCheck size={13} style={{ color: MUSTARD }} />
            <span className="font-mono-rm uppercase tracking-widest text-[10px]" style={{ fontWeight: 800 }}>
              · Browsing the Store as admin
            </span>
            <span className="text-[11px]" style={{ color: "rgba(255,255,255,0.72)" }}>
              — actions taken here (save · send intro) will be attributed to your admin account.
            </span>
          </div>
        </div>
      )}

      {/* Body */}
      <div className="max-w-7xl mx-auto px-4 md:px-8 py-5 grid grid-cols-1 lg:grid-cols-[280px_1fr] gap-5">
        {/* Sidebar filters */}
        <aside data-testid="store-filters">
          <div
            className="rounded-md p-4 sticky top-4"
            style={{
              background: "white",
              border: "1.5px solid #CBD5E1",
              boxShadow: CARD_DEEP_SHADOW,
            }}
          >
            <div className="flex items-center justify-between mb-3">
              <div className="inline-flex items-center gap-1.5 font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: NAVY, fontWeight: 800 }}>
                <Filter size={12} style={{ color: ORANGE }} /> · Filters
              </div>
              <button
                type="button"
                onClick={() => setFiltersOpen((o) => !o)}
                className="lg:hidden text-[11px] font-mono-rm uppercase tracking-widest"
                style={{ color: "#475569" }}
                data-testid="store-filters-toggle"
              >
                {filtersOpen ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
              </button>
            </div>
            <div className={filtersOpen ? "" : "hidden lg:block"}>
              <FilterInput
                label="Keyword"
                value={q}
                onChange={setQ}
                placeholder="role, headline, or skill"
                icon={<Search size={12} />}
                testId="store-filter-q"
              />
              <FilterInput
                label="Location"
                value={location}
                onChange={setLocation}
                placeholder="City or state"
                icon={<MapPin size={12} />}
                testId="store-filter-location"
              />
              <div className="grid grid-cols-2 gap-2">
                <FilterInput
                  label="Min years"
                  value={minYears}
                  onChange={(v) => setMinYears(v)}
                  type="number"
                  icon={<Users size={12} />}
                  testId="store-filter-min-years"
                />
                <FilterInput
                  label="Max years"
                  value={maxYears}
                  onChange={(v) => setMaxYears(v)}
                  type="number"
                  placeholder="—"
                  testId="store-filter-max-years"
                />
              </div>

              {/* Skills — chips + input */}
              <FilterLabel>Skills</FilterLabel>
              <div className="rounded" style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC", padding: "6px" }}>
                <div className="flex flex-wrap gap-1 mb-1">
                  {skills.map((s, i) => (
                    <span
                      key={`sk-${i}`}
                      className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                      style={{ background: CREAM, color: NAVY, border: `1px solid ${ORANGE}`, fontWeight: 700 }}
                      data-testid={`store-filter-skill-${i}`}
                    >
                      {s}
                      <button type="button" onClick={() => removeSkill(s)} aria-label={`Remove ${s}`}>
                        <X size={9} style={{ color: ORANGE }} />
                      </button>
                    </span>
                  ))}
                </div>
                <input
                  type="text"
                  value={skillsInput}
                  onChange={(e) => setSkillsInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === ",") {
                      e.preventDefault();
                      addSkill(skillsInput);
                      setSkillsInput("");
                    }
                  }}
                  onBlur={() => { if (skillsInput) { addSkill(skillsInput); setSkillsInput(""); } }}
                  placeholder="Add skill + press Enter"
                  className="w-full text-[12px] px-1 py-1 border-0 bg-transparent outline-none"
                  data-testid="store-filter-skills-input"
                />
              </div>

              <FilterSelect
                label="Availability"
                value={availability}
                onChange={setAvailability}
                options={AVAILABILITY_OPTIONS}
                testId="store-filter-availability"
              />
              <FilterSelect
                label="Work mode"
                value={remoteMode}
                onChange={setRemoteMode}
                options={REMOTE_MODE_OPTIONS}
                icon={<Home size={12} />}
                testId="store-filter-remote"
              />

              <FilterLabel><DollarSign size={11} className="inline mr-1" style={{ color: "#64748B" }} />Salary range (USD/yr)</FilterLabel>
              <div className="grid grid-cols-2 gap-2">
                <input
                  type="number"
                  value={salaryMin}
                  onChange={(e) => setSalaryMin(e.target.value)}
                  placeholder="Min"
                  className="w-full rounded px-2 py-1.5 text-[12px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                  data-testid="store-filter-salary-min"
                />
                <input
                  type="number"
                  value={salaryMax}
                  onChange={(e) => setSalaryMax(e.target.value)}
                  placeholder="Max"
                  className="w-full rounded px-2 py-1.5 text-[12px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                  data-testid="store-filter-salary-max"
                />
              </div>

              {/* Certs */}
              <FilterLabel><Award size={11} className="inline mr-1" style={{ color: "#64748B" }} />Certifications</FilterLabel>
              <div className="flex flex-wrap gap-1 mb-1">
                {certs.map((c, i) => (
                  <span
                    key={`c-${i}`}
                    className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
                    style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #34D399", fontWeight: 700 }}
                  >
                    {c}
                    <button type="button" onClick={() => removeCert(c)} aria-label={`Remove ${c}`}>
                      <X size={9} style={{ color: "#065F46" }} />
                    </button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <input
                  type="text"
                  value={certsInput}
                  onChange={(e) => setCertsInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addCert(); } }}
                  placeholder="e.g. AWS SAA"
                  className="flex-1 rounded px-2 py-1.5 text-[12px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
                  data-testid="store-filter-cert-input"
                />
                <button
                  type="button"
                  onClick={addCert}
                  className="text-[10px] font-mono-rm uppercase tracking-widest px-2 rounded"
                  style={{ background: NAVY, color: "white", fontWeight: 700 }}
                  data-testid="store-filter-cert-add"
                >
                  Add
                </button>
              </div>

              {/* Featured only */}
              <label className="flex items-center gap-2 mt-3 cursor-pointer" data-testid="store-filter-featured-label">
                <input
                  type="checkbox"
                  checked={featuredOnly}
                  onChange={(e) => setFeaturedOnly(e.target.checked)}
                  data-testid="store-filter-featured"
                />
                <span className="text-[12px] inline-flex items-center gap-1" style={{ color: NAVY, fontWeight: 600 }}>
                  <Star size={11} style={{ color: MUSTARD, fill: MUSTARD }} /> Featured only
                </span>
              </label>

              {/* Actions */}
              <div className="mt-4 flex gap-2">
                <button
                  type="button"
                  onClick={runSearch}
                  disabled={busy}
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded px-3 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                  style={{
                    background: ORANGE, color: "white",
                    border: `2px solid ${NAVY}`,
                    boxShadow: `2px 2px 0 0 ${NAVY}`,
                    fontWeight: 700, cursor: busy ? "wait" : "pointer",
                  }}
                  data-testid="store-filter-apply"
                >
                  <Search size={11} /> {busy ? "Searching…" : "Apply filters"}
                </button>
                <button
                  type="button"
                  onClick={clearFilters}
                  className="rounded px-3 py-2 font-mono-rm uppercase text-[11px] tracking-widest"
                  style={{ background: "white", color: NAVY, border: `1.5px solid ${NAVY}`, fontWeight: 700 }}
                  data-testid="store-filter-clear"
                >
                  Clear
                </button>
              </div>
            </div>
          </div>
        </aside>

        {/* Main column */}
        <main>
          {/* Skills extractor widget */}
          <div className="mb-4">
            <SkillsExtractorWidget
              onApplySkills={addSkills}
              defaultOpen={false}
              testIdPrefix="store-skills-extractor"
            />
          </div>

          {/* Sort + view toggle strip */}
          <div className="flex items-center justify-between flex-wrap gap-3 mb-3" data-testid="store-toolbar">
            <div className="font-mono-rm text-[10px] uppercase tracking-widest" style={{ color: MUSTARD, fontWeight: 700 }}>
              {count === null ? "· Loading…" :
                `· ${count} candidate${count === 1 ? "" : "s"} matched`}
            </div>
            <div className="flex items-center gap-2">
              <label className="inline-flex items-center gap-1 text-[10px] font-mono-rm uppercase tracking-widest" style={{ color: "rgba(255,255,255,0.72)", fontWeight: 700 }}>
                <span>Sort</span>
                <select
                  value={sort}
                  onChange={(e) => { setSort(e.target.value); }}
                  className="rounded px-2 py-1 text-[11px]"
                  style={{ border: "1.5px solid #CBD5E1", background: "white", color: NAVY }}
                  data-testid="store-sort"
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
              <div className="inline-flex rounded overflow-hidden" style={{ border: `1.5px solid ${MUSTARD}` }}>
                <button
                  type="button"
                  onClick={() => setView("grid")}
                  aria-pressed={view === "grid"}
                  className="px-2 py-1"
                  style={{ background: view === "grid" ? MUSTARD : "rgba(255,255,255,0.06)", color: view === "grid" ? NAVY : MUSTARD }}
                  data-testid="store-view-grid"
                >
                  <Grid size={12} />
                </button>
                <button
                  type="button"
                  onClick={() => setView("list")}
                  aria-pressed={view === "list"}
                  className="px-2 py-1"
                  style={{ background: view === "list" ? MUSTARD : "rgba(255,255,255,0.06)", color: view === "list" ? NAVY : MUSTARD }}
                  data-testid="store-view-list"
                >
                  <List size={12} />
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div
              className="rounded-md p-3 mb-3 text-[12px]"
              style={{ background: "#FEE2E2", border: "1px solid #FCA5A5", color: "#7F1D1D" }}
              data-testid="store-error"
            >
              {error}
            </div>
          )}

          {/* Results — grid or list */}
          {view === "grid" ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="store-results-grid">
              {results.map((r, i) => (
                <StoreCard
                  key={r.public_id || `anon-${i}`}
                  r={r}
                  isVerified={isVerified}
                  saved={r.public_id ? savedSet.has(r.public_id) : false}
                  readOnly={!!employer?.is_admin_impersonating}
                  onSaveChange={(nowSaved) => {
                    if (!r.public_id) return;
                    setSavedSet((prev) => {
                      const next = new Set(prev);
                      if (nowSaved) next.add(r.public_id);
                      else next.delete(r.public_id);
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          ) : (
            <div className="space-y-3" data-testid="store-results-list">
              {results.map((r, i) => (
                <StoreListRow
                  key={r.public_id || `anon-${i}`}
                  r={r}
                  isVerified={isVerified}
                  saved={r.public_id ? savedSet.has(r.public_id) : false}
                  readOnly={!!employer?.is_admin_impersonating}
                  onSaveChange={(nowSaved) => {
                    if (!r.public_id) return;
                    setSavedSet((prev) => {
                      const next = new Set(prev);
                      if (nowSaved) next.add(r.public_id);
                      else next.delete(r.public_id);
                      return next;
                    });
                  }}
                />
              ))}
            </div>
          )}

          {results.length === 0 && !busy && (
            <div
              className="rounded-md py-12 text-center"
              style={{
                background: "rgba(255,255,255,0.06)",
                border: `1px dashed ${MUSTARD}`,
                backdropFilter: "blur(6px)",
              }}
              data-testid="store-empty-state"
            >
              <Sparkles size={22} style={{ color: MUSTARD }} className="mx-auto mb-2" />
              <div className="font-mono-rm text-[10px] uppercase tracking-widest mb-1" style={{ color: MUSTARD, fontWeight: 700 }}>
                · No matches
              </div>
              <div className="text-[13px]" style={{ color: "rgba(255,255,255,0.78)" }}>
                Try broadening your search or removing filters.
              </div>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ─── Small filter helpers ───
function FilterLabel({ children }) {
  return (
    <div className="font-mono-rm text-[9px] uppercase tracking-widest mt-3 mb-1" style={{ color: "#64748B", fontWeight: 700 }}>
      · {children}
    </div>
  );
}
function FilterInput({ label, value, onChange, placeholder, type = "text", icon, testId }) {
  return (
    <div className="mb-1">
      <FilterLabel>{label}</FilterLabel>
      <div className="relative">
        {icon && (
          <span className="absolute left-2 top-1/2 -translate-y-1/2" style={{ color: "#94A3B8" }}>
            {icon}
          </span>
        )}
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded px-2 py-1.5 text-[12px]"
          style={{ paddingLeft: icon ? "1.6rem" : undefined, border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
          data-testid={testId}
        />
      </div>
    </div>
  );
}
function FilterSelect({ label, value, onChange, options, icon, testId }) {
  return (
    <div className="mb-1">
      <FilterLabel>{icon && <span className="inline-block mr-1" style={{ color: "#64748B" }}>{icon}</span>}{label}</FilterLabel>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded px-2 py-1.5 text-[12px]"
        style={{ border: "1.5px solid #CBD5E1", background: "#F8FAFC" }}
        data-testid={testId}
      >
        {options.map((o) => (
          <option key={o.value} value={o.value}>{o.label}</option>
        ))}
      </select>
    </div>
  );
}

// ─── Card components ───
function AvailabilityBadge({ availability }) {
  if (!availability) return null;
  const map = {
    actively_looking: { label: "Actively looking", bg: "#DCFCE7", color: "#065F46", dot: "#10B981" },
    open_to_offers:   { label: "Open to offers",  bg: "#FEF3C7", color: "#78350F", dot: "#F59E0B" },
    not_looking:      { label: "Not looking",      bg: "#F1F5F9", color: "#475569", dot: "#94A3B8" },
  };
  const s = map[availability];
  if (!s) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
      style={{ background: s.bg, color: s.color, fontWeight: 700 }}
    >
      <span style={{ width: 5, height: 5, borderRadius: "50%", background: s.dot }} />
      {s.label}
    </span>
  );
}
function RemoteBadge({ mode }) {
  if (!mode || mode === "any") return null;
  const label = { remote: "Remote", hybrid: "Hybrid", onsite: "On-site" }[mode];
  if (!label) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
      style={{ background: "#EFF6FF", color: "#1E40AF", border: "1px solid #BFDBFE", fontWeight: 700 }}
    >
      <Home size={9} /> {label}
    </span>
  );
}
function FeaturedBadge({ featured }) {
  if (!featured) return null;
  return (
    <span
      className="inline-flex items-center gap-1 text-[9px] font-mono-rm uppercase tracking-widest px-1.5 py-0.5 rounded"
      style={{ background: MUSTARD, color: NAVY, fontWeight: 800, border: `1px solid ${NAVY}` }}
      data-testid="store-featured-badge"
    >
      <Star size={9} style={{ fill: NAVY }} /> Featured
    </span>
  );
}

function SuperpowerPill({ text, avatar }) {
  if (!text) return null;
  return (
    <div
      className="mt-1 rounded p-2 flex items-start gap-2"
      style={{ background: CREAM, border: `1.5px dashed ${ORANGE}` }}
      data-testid="store-superpower"
    >
      {avatar ? (
        <img src={avatar} alt="superpower"
             style={{ width: 32, height: 32, borderRadius: 6, border: `1.5px solid ${NAVY}`, objectFit: "cover" }} />
      ) : (
        <span
          className="inline-flex items-center justify-center rounded"
          style={{ width: 32, height: 32, background: NAVY, color: MUSTARD, border: `1.5px solid ${NAVY}` }}
        >
          <Zap size={16} />
        </span>
      )}
      <div className="min-w-0">
        <div className="font-mono-rm text-[9px] uppercase tracking-widest" style={{ color: ORANGE, fontWeight: 700 }}>
          · Superpower
        </div>
        <div className="text-[12px] leading-snug" style={{ color: NAVY, fontWeight: 600 }}>
          {text}
        </div>
      </div>
    </div>
  );
}

function SalaryLine({ min, max, currency }) {
  if (!min && !max) return null;
  const c = currency || "USD";
  const fmt = (v) => `${c === "USD" ? "$" : c + " "}${Number(v).toLocaleString()}`;
  const label = min && max ? `${fmt(min)}–${fmt(max)}` : (min ? `${fmt(min)}+` : `up to ${fmt(max)}`);
  return (
    <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#475569" }}>
      <DollarSign size={10} /> {label}
    </span>
  );
}

function CardHeader({ r, isVerified, saved, onSaveChange, readOnly }) {
  const cardTitle = (
    <div>
      <div className="font-display uppercase leading-tight" style={{ fontSize: "1.05rem", color: NAVY, letterSpacing: "-0.01em" }}>
        {r.name_display}
      </div>
      {r.headline && <div className="text-[12px]" style={{ color: "#334155" }}>{r.headline}</div>}
    </div>
  );
  return (
    <div className="flex items-start justify-between gap-2 mb-2">
      {cardTitle}
      <div className="flex flex-col items-end gap-1">
        {isVerified && r.public_id && (
          <SaveCandidateButton
            publicId={r.public_id}
            initialSaved={saved}
            variant="icon"
            onChange={onSaveChange}
            readOnly={readOnly}
            testIdPrefix="store-save"
          />
        )}
        <FeaturedBadge featured={r.featured} />
        <span
          className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
          style={{ background: "#FEF3C7", color: "#CA8A04", fontWeight: 700 }}
        >
          {r.years_experience || 0} yrs
        </span>
        {!isVerified && (
          <span
            className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
            style={{ background: "#FEE2E2", color: "#7F1D1D", fontWeight: 700 }}
          >
            Preview
          </span>
        )}
      </div>
    </div>
  );
}

function StoreCard({ r, isVerified, saved, onSaveChange, readOnly }) {
  const inner = (
    <>
      <CardHeader r={r} isVerified={isVerified} saved={saved} onSaveChange={onSaveChange} readOnly={readOnly} />
      <SuperpowerPill text={r.superpower} avatar={r.superpower_avatar_url} />
      <div className="flex flex-wrap items-center gap-2 mt-2">
        {r.location && (
          <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#64748B" }}>
            <MapPin size={11} /> {r.location}
          </span>
        )}
        <SalaryLine min={r.salary_min} max={r.salary_max} currency={r.salary_currency} />
        <AvailabilityBadge availability={r.availability} />
        <RemoteBadge mode={r.remote_mode} />
      </div>
      {r.summary && (
        <p className="text-[12px] leading-snug mt-2 mb-2" style={{ color: "#334155" }}>
          {(r.summary || "").slice(0, 180)}{(r.summary || "").length > 180 && "…"}
        </p>
      )}
      {r.skills?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {r.skills.slice(0, 6).map((s, i) => (
            <span
              key={i}
              className="text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: "#F1F5F9", color: "#334155", border: "1px solid #E2E8F0" }}
            >
              {s}
            </span>
          ))}
        </div>
      )}
      {r.certifications?.length > 0 && (
        <div className="flex flex-wrap gap-1 mb-2">
          {r.certifications.slice(0, 3).map((c, i) => (
            <span
              key={i}
              className="inline-flex items-center gap-0.5 text-[10px] px-1.5 py-0.5 rounded"
              style={{ background: "#ECFDF5", color: "#065F46", border: "1px solid #A7F3D0" }}
            >
              <Award size={9} /> {c}
            </span>
          ))}
        </div>
      )}
      <div className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest" style={{ color: ORANGE, fontWeight: 700 }}>
        {isVerified ? "View résumé" : "Verify to view"} <ArrowRight size={11} />
      </div>
    </>
  );

  const commonStyle = {
    background: "white", border: "1.5px solid #CBD5E1",
    boxShadow: "3px 3px 0 0 rgba(255,193,69,0.55), 0 8px 24px rgba(0,0,0,0.3)",
    textDecoration: "none", color: "inherit",
  };
  const hoverIn = (e) => {
    e.currentTarget.style.boxShadow = `5px 5px 0 0 ${MUSTARD}, 0 12px 36px rgba(0,0,0,0.45)`;
    e.currentTarget.style.borderColor = NAVY;
  };
  const hoverOut = (e) => {
    e.currentTarget.style.boxShadow = "3px 3px 0 0 rgba(255,193,69,0.55), 0 8px 24px rgba(0,0,0,0.3)";
    e.currentTarget.style.borderColor = "#CBD5E1";
  };

  if (isVerified && r.public_id) {
    return (
      <Link
        to={`/r/${r.public_id}`}
        className="block rounded-md p-4 transition-transform hover:translate-y-[-2px]"
        style={commonStyle}
        onMouseEnter={hoverIn}
        onMouseLeave={hoverOut}
        data-testid={`store-card-${r.public_id}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div
      className="block rounded-md p-4 cursor-not-allowed"
      style={{ ...commonStyle, opacity: 0.9 }}
      data-testid="store-card-anon"
    >
      {inner}
    </div>
  );
}

function StoreListRow({ r, isVerified, saved, onSaveChange, readOnly }) {
  const rowStyle = {
    background: "white", border: "1.5px solid #CBD5E1",
    boxShadow: "3px 3px 0 0 rgba(255,193,69,0.55), 0 8px 24px rgba(0,0,0,0.3)",
    textDecoration: "none", color: "inherit",
  };
  const inner = (
    <div className="flex items-start gap-3 p-4">
      <div style={{ width: 44, height: 44, flex: "0 0 44px" }} className="rounded flex items-center justify-center">
        {r.superpower_avatar_url ? (
          <img src={r.superpower_avatar_url} alt="candidate"
               style={{ width: 44, height: 44, borderRadius: 6, border: `1.5px solid ${NAVY}`, objectFit: "cover" }} />
        ) : (
          <span
            className="inline-flex items-center justify-center rounded"
            style={{ width: 44, height: 44, background: NAVY, color: MUSTARD, border: `1.5px solid ${NAVY}` }}
          >
            <Zap size={20} />
          </span>
        )}
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap">
          <div className="font-display uppercase leading-tight" style={{ fontSize: "1.05rem", color: NAVY, letterSpacing: "-0.01em" }}>
            {r.name_display}
          </div>
          <FeaturedBadge featured={r.featured} />
          <AvailabilityBadge availability={r.availability} />
          <RemoteBadge mode={r.remote_mode} />
          {!isVerified && (
            <span
              className="font-mono-rm text-[9px] uppercase tracking-widest px-1.5 py-0.5 rounded"
              style={{ background: "#FEE2E2", color: "#7F1D1D", fontWeight: 700 }}
            >
              Preview
            </span>
          )}
        </div>
        {r.headline && <div className="text-[12px]" style={{ color: "#334155" }}>{r.headline}</div>}
        {r.superpower && (
          <div className="text-[12px] mt-1" style={{ color: ORANGE, fontWeight: 600 }}>
            ⚡ {r.superpower}
          </div>
        )}
        <div className="flex flex-wrap items-center gap-2 mt-1">
          {r.location && (
            <span className="inline-flex items-center gap-1 text-[11px]" style={{ color: "#64748B" }}>
              <MapPin size={11} /> {r.location}
            </span>
          )}
          <span className="text-[11px]" style={{ color: "#64748B" }}>{r.years_experience || 0} yrs</span>
          <SalaryLine min={r.salary_min} max={r.salary_max} currency={r.salary_currency} />
        </div>
        {r.skills?.length > 0 && (
          <div className="flex flex-wrap gap-1 mt-1">
            {r.skills.slice(0, 8).map((s, i) => (
              <span
                key={i}
                className="text-[10px] px-1.5 py-0.5 rounded"
                style={{ background: "#F1F5F9", color: "#334155", border: "1px solid #E2E8F0" }}
              >
                {s}
              </span>
            ))}
          </div>
        )}
      </div>
      <div className="hidden md:flex flex-col items-end gap-2 self-center">
        {isVerified && r.public_id && (
          <SaveCandidateButton
            publicId={r.public_id}
            initialSaved={saved}
            variant="icon"
            onChange={onSaveChange}
            testIdPrefix="store-save"
          />
        )}
        <span className="inline-flex items-center gap-1 text-[11px] font-mono-rm uppercase tracking-widest" style={{ color: ORANGE, fontWeight: 700 }}>
          {isVerified ? "View" : "Verify"} <ArrowRight size={11} />
        </span>
      </div>
    </div>
  );

  if (isVerified && r.public_id) {
    return (
      <Link
        to={`/r/${r.public_id}`}
        className="block rounded-md transition-transform hover:translate-y-[-2px]"
        style={rowStyle}
        onMouseEnter={(e) => { e.currentTarget.style.boxShadow = `5px 5px 0 0 ${MUSTARD}, 0 12px 36px rgba(0,0,0,0.45)`; e.currentTarget.style.borderColor = NAVY; }}
        onMouseLeave={(e) => { e.currentTarget.style.boxShadow = "3px 3px 0 0 rgba(255,193,69,0.55), 0 8px 24px rgba(0,0,0,0.3)"; e.currentTarget.style.borderColor = "#CBD5E1"; }}
        data-testid={`store-row-${r.public_id}`}
      >
        {inner}
      </Link>
    );
  }
  return (
    <div className="block rounded-md cursor-not-allowed" style={{ ...rowStyle, opacity: 0.9 }} data-testid="store-row-anon">
      {inner}
    </div>
  );
}
